import {
  put,
  select,
  takeLatest,
  debounce,
  takeEvery,
} from 'redux-saga/effects';
import qs from 'qs';
import { gasketsActions, gasketsActions as actions } from '.';
import { selectGasketByErp, selectNonStandardGasket } from './selectors';
import { PayloadAction } from '@reduxjs/toolkit';
import { GasketPricePayload, SGAddToCartPayload } from './types';
import {
  selectErpSystem,
  selectLanguage,
  selectLanguageId,
} from '../../erpSystem/slice/selectors';
import { selectUser } from '../../user/slice/selectors';
import { selectShape } from '../../shapeVisualization/slice/selectors';
import { transformStandardGasketsDTO, transformStockDTO } from './utils';
import { notificationsActions } from '../../notifications/slice';
import { selectSingleItemData } from '../../singleItem/slice/selectors';
import { shoppingCartActions } from '../../shoppingCart/slice';

function* fetchPrice(action: PayloadAction<GasketPricePayload>) {
  const {
    payload: { erpPartNumber, ...rest },
  } = action;
  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);
  const {
    data: { customerNumber },
  } = yield select(selectUser);
  const shape = yield select(selectShape);
  if (erpPartNumber) {
    const gasket = yield select(selectGasketByErp(erpPartNumber));
    try {
      const body = {
        erpPartNumber,
        configuration: gasket.configuration.toString(),
        quantity: gasket.price.itemQuantity,
        country: erpSystem,
        language,
        customerNumber,
        shape,
        ...rest,
      };

      const bodyString = JSON.stringify(body);
      const priceResponse = yield fetch('/gasket-configurator/api/price', {
        body: bodyString,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (priceResponse.status !== 200) {
        return yield put(
          actions.fetchPriceByErpFail({
            error: 'failed to fetch',
            erpPartNumber,
          }),
        );
      }
      const price = yield priceResponse.json();
      if (!price || !price[0].priceData.netPrice) {
        return yield put(
          actions.fetchPriceByErpFail({
            error: 'no price for this item',
            erpPartNumber,
          }),
        );
      }
      yield put(
        actions.fetchPriceByErpSuccess({
          erpPartNumber,
          data: price[0].priceData,
        }),
      );
    } catch (e: any) {
      yield put(
        actions.fetchPriceByErpFail({
          error:
            e.message || e.status || e.statusText || 'failed to fetch price',
          erpPartNumber,
        }),
      );
    }
  }
}

function* fetchStandardGaskets() {
  const { innerDiameter, outerDiameter, gskCompound } = yield select(
    selectNonStandardGasket,
  );
  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);

  const queryString = qs.stringify({
    innerDiameter,
    outerDiameter,
    gskCompoundId: gskCompound,
    erpSystem,
    lang: language,
  });

  try {
    const standardGasketsResponse = yield fetch(
      `/gasket-configurator/api/standardGaskets?${queryString}`,
    );

    if (standardGasketsResponse.status === 200) {
      const standardGaskets = yield standardGasketsResponse.json();
      const transformedGaskets = transformStandardGasketsDTO(standardGaskets);
      return yield put(actions.fetchStandardGasketsSuccess(transformedGaskets));
    }
  } catch (e) {
    console.log(e);
  }
}

function* setQuantity(
  action: PayloadAction<{
    quantity: number;
    values: GasketPricePayload;
    isStandard: boolean;
  }>,
) {
  if (!action.payload.isStandard) {
    // we will get here after the quantity is updated in the reducer, so we just call the price fetch with payload
    yield put(actions.fetchPriceByErp(action.payload.values));
  }
}

function* clearConfiguredPrice(action: PayloadAction<boolean>) {
  const shape = yield select(selectShape);
  const nonstandard = yield select(selectNonStandardGasket);
  const item = yield select(selectSingleItemData);
  if (action.payload === false) {
    yield put(actions.clearGaskets());
  }
  if (
    action.payload === true &&
    shape === 'ring' &&
    (nonstandard?.numberOfBoltHoles === '0' || item?.numberOfBoltHoles === '0')
  ) {
    yield put(actions.fetchStandardGaskets());
  }
}

function* addToCartStandardGasket(action: PayloadAction<string>) {
  const gasket = yield select(selectGasketByErp(action.payload));
  const erpSystem = yield select(selectErpSystem);
  const languageId = yield select(selectLanguageId);
  if (gasket) {
    const body: SGAddToCartPayload = {
      erpPartNo: gasket.erpPartNumber,
      quantity: gasket.price.itemQuantity,
      targetSystem: 'SEL_GAS',
      erpSystem,
      locale: languageId,
    };
    try {
      const addToCartRes = yield fetch(
        `/gasket-configurator/api/standardAddToCart?${qs.stringify(body)}`,
      );
      if (addToCartRes.status === 200) {
        yield put(
          notificationsActions.setNotification({
            customItemDescription: gasket.description,
            timeoutId: Date.now().toString(),
            status: 'success',
            notificationKey: 'successAddToCart',
            isDismissed: false,
          }),
        );
        yield put(
          gasketsActions.addToCartStandardGasketSuccess(action.payload),
        );
        yield put(shoppingCartActions.getShoppingCart());
      }
    } catch (e) {
      console.log(e);
    }
  }
}

function* fetchStandardGasketPrice(action: PayloadAction<string>) {
  const erpSystem = yield select(selectErpSystem);
  const languageId = yield select(selectLanguageId);
  const query = {
    erpSystem,
    languageId,
    itemsNumbers: action.payload,
    targetSystem: 'SEL_GAS',
    quantity: 3,
  };
  try {
    const priceResponse = yield fetch(
      `/gasket-configurator/api/standardPrice?${qs.stringify(query)}`,
    );
    if (priceResponse.status !== 200) {
      return yield put(
        actions.fetchPriceByErpFail({
          error: 'failed to fetch price',
          erpPartNumber: action.payload,
        }),
      );
    }
    const { items } = yield priceResponse.json();
    const priceData = {
      netPrice: items[0].priceRaw,
      netValue: items[0].priceRaw,
    };
    yield put(
      gasketsActions.fetchPriceByErpSuccess({
        erpPartNumber: action.payload,
        data: priceData,
      }),
    );
  } catch (e: any) {
    return yield put(
      actions.fetchPriceByErpFail({
        error: 'failed to fetch price',
        erpPartNumber: action.payload,
      }),
    );
  }
}

function* fetchStockStatus(action: PayloadAction<string>) {
  const erpSystem = yield select(selectErpSystem);
  const input = {
    itemNumber: action.payload,
    unitCode: 'PCE',
  };

  const bodyString = JSON.stringify(input);
  try {
    const stockResponse = yield fetch(
      `/gasket-configurator/api/stockStatus?erpSystem=${erpSystem}`,
      {
        body: bodyString,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    if (stockResponse.status !== 200) {
      return yield put(
        gasketsActions.fetchStockStatusByErpFail({
          error: 'infoUnavailable',
          erpPartNumber: action.payload,
        }),
      );
    }
    const stock = yield stockResponse.json();
    const stockData = transformStockDTO(stock);
    yield put(
      gasketsActions.fetchStockStatusByErpSuccess({
        data: stockData,
        erpPartNumber: action.payload,
      }),
    );
  } catch (e: any) {
    return yield put(
      gasketsActions.fetchStockStatusByErpFail({
        error: 'infoUnavailable',
        erpPartNumber: action.payload,
      }),
    );
  }
}

export function* gasketsSaga() {
  yield takeLatest([actions.fetchPriceByErp.type], fetchPrice);
  yield takeEvery([actions.fetchStandardGasketPrice], fetchStandardGasketPrice);
  yield debounce(500, [actions.setQuantityByErp.type], setQuantity);
  yield takeLatest([gasketsActions.setShowPriceResults], clearConfiguredPrice);
  yield takeLatest([gasketsActions.fetchStandardGaskets], fetchStandardGaskets);
  yield takeLatest(
    [gasketsActions.addToCartStandardGasket],
    addToCartStandardGasket,
  );
  yield takeEvery([gasketsActions.fetchStockStatusByErp], fetchStockStatus);
}
